import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
    Loader2,
    AlertTriangle,
    ImageIcon,
    Sparkles,
    ChevronLeft,
    ChevronRight,
    Check,
    Type,
    Plus,
    X,
} from 'lucide-react';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';
import type {
    ButtonInput,
    UtilityTemplateCreatePayload,
    UtilityTemplateImageSource,
} from '@/services/api/utility-templates';
import ButtonsEditor, { LocalInput } from './ButtonsEditor';
import UtilityTemplateMessengerPreview from './UtilityTemplateMessengerPreview';
import CompactPageSelector from './CompactPageSelector';
import MediaLibraryModal from '@/pages/Meta/Flow/Builder/components/MediaLibraryModal';
import type { MediaResponse } from '@/types/media';

const HIGH_RISK_TERMS = ['gratis', 'grátis', 'ganhe', 'free', 'offer', 'win', 'promo', 'last chance'];

function detectHighRisk(text: string): boolean {
    const lower = text.toLowerCase();
    return HIGH_RISK_TERMS.some((term) => lower.includes(term));
}

/** Dados para pré-preencher o modal ao duplicar um template existente. */
export interface CreateTemplateInitialData {
    template_name: string;
    source_language: string | null;
    title: string;
    subtitle: string;
    buttons: ButtonInput[];
    image_url: string | null;
    page_ids?: string[];
}

interface CreateTemplateDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    pages: MetaPage[];
    onSubmit: (
        payload: UtilityTemplateCreatePayload,
        /** Ausente no caso "só texto" (Foto desabilitada) — sem imagem. */
        imageSource: UtilityTemplateImageSource | undefined,
        /** Bloco "Texto" opcional — cria um 2º template independente (sem imagem), mesmas páginas. */
        secondaryPayload?: UtilityTemplateCreatePayload
    ) => void;
    isPending: boolean;
    initialData?: CreateTemplateInitialData | null;
}

interface FormState {
    selectedPageIds: number[];
    template_name: string;
    source_language: string;
    title: string;
    subtitle: string;
    buttons: ButtonInput[];
}

const EMPTY_FORM: FormState = {
    selectedPageIds: [],
    template_name: '',
    source_language: '',
    title: '',
    subtitle: '',
    buttons: [],
};

function formFromInitialData(data: CreateTemplateInitialData, pages: MetaPage[]): FormState {
    const pageIds = data.page_ids
        ? pages.filter((p) => data.page_ids!.includes(p.page_id)).map((p) => p.id)
        : [];
    return {
        selectedPageIds: pageIds,
        template_name: `${data.template_name}_copy`,
        source_language: data.source_language ?? '',
        title: data.title,
        subtitle: data.subtitle,
        buttons: data.buttons,
    };
}

const TEMPLATE_NAME_RE = /^[a-z0-9_]*$/;

const STEPS = [
    { number: 1, title: 'Páginas' },
    { number: 2, title: 'Conteúdo' },
    { number: 3, title: 'Revisar' },
];
const TOTAL_STEPS = STEPS.length;

export default function CreateTemplateDialog({
    open,
    onOpenChange,
    pages,
    onSubmit,
    isPending,
    initialData,
}: CreateTemplateDialogProps) {
    const [currentStep, setCurrentStep] = useState(1);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [selectedMedia, setSelectedMedia] = useState<MediaResponse | null>(null);
    const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
    const [forceSubmit, setForceSubmit] = useState(false);
    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
    // Nenhum bloco pré-selecionado — igual ao "Monte sua Mensagem" do
    // Broadcast: começa vazio, usuário escolhe via "Adicionar Bloco".
    // Bloco "Foto": title + subtitle + mídia + botões (opcional).
    const [primaryEnabled, setPrimaryEnabled] = useState(false);
    // Bloco "Texto" opcional — vira um 2º utility template independente
    // (sem imagem, sem botões), criado com as mesmas páginas do principal.
    // Se o bloco "Foto" estiver desabilitado, vira o PRÓPRIO template principal.
    const [secondaryEnabled, setSecondaryEnabled] = useState(false);
    const [secondaryTitle, setSecondaryTitle] = useState('');
    // Seletor "Adicionar Bloco" (Foto / Texto), mesmo padrão do Broadcast.
    const [isAddBlockMenuOpen, setIsAddBlockMenuOpen] = useState(false);
    // Confirmação ao remover o bloco "Foto" já preenchido (perde conteúdo).
    const [confirmRemovePrimary, setConfirmRemovePrimary] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCurrentStep(1);
        if (initialData) {
            setForm(formFromInitialData(initialData, pages));
            setOriginalImageUrl(initialData.image_url);
        } else {
            setForm(EMPTY_FORM);
            setOriginalImageUrl(null);
        }
        setSelectedMedia(null);
        setForceSubmit(false);
        // Ao duplicar, o template original sempre tem o bloco "Foto"
        // preenchido (title/subtitle/image) — abrir o bloco já com esse
        // conteúdo, em vez de cair na tela vazia de "Adicionar Bloco".
        setPrimaryEnabled(!!initialData);
        setSecondaryEnabled(false);
        setSecondaryTitle('');
        setIsAddBlockMenuOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialData]);

    const isHighRisk = detectHighRisk(form.title + ' ' + form.subtitle + ' ' + secondaryTitle);

    const selectedPages = pages.filter((p) => form.selectedPageIds.includes(p.id));
    const languagesInvolved = Array.from(new Set(selectedPages.map((p) => p.language || 'en_US')));

    const handleMediaSelect = (media: MediaResponse) => {
        setSelectedMedia(media);
        setOriginalImageUrl(null);
        setIsMediaLibraryOpen(false);
    };

    const handleClose = () => {
        setCurrentStep(1);
        setForm(EMPTY_FORM);
        setSelectedMedia(null);
        setOriginalImageUrl(null);
        setForceSubmit(false);
        setPrimaryEnabled(false);
        setSecondaryEnabled(false);
        setSecondaryTitle('');
        setIsAddBlockMenuOpen(false);
        onOpenChange(false);
    };

    const hasImage = selectedMedia !== null || originalImageUrl !== null;

    // Um botão é válido se tiver texto e, conforme o tipo, a URL (direta ou
    // por url_entries com peso) ou o payload — a Meta rejeita (400,
    // subcode 2388050) um botão URL sem URL nenhuma preenchida.
    const buttonsValid = form.buttons.every((b) => {
        if (!b.text?.trim()) return false;
        if (b.type === 'URL') {
            const hasUrlBase = !!b.url_base?.trim();
            const hasUrlEntries = (b.url_entries ?? []).some((e) => !!e.url?.trim());
            return hasUrlBase || hasUrlEntries;
        }
        if (b.type === 'POSTBACK') return !!b.payload?.trim();
        return true;
    });

    // Bloco "Foto" válido = title + subtitle + mídia (comportamento atual, inalterado).
    const primaryValid = !!form.title.trim() && !!form.subtitle.trim() && selectedMedia !== null && buttonsValid;
    // Bloco "Texto" válido = mensagem preenchida.
    const secondaryValid = !!secondaryTitle.trim();

    const canProceed = (): boolean => {
        switch (currentStep) {
            case 1:
                return form.selectedPageIds.length > 0;
            case 2: {
                const hasAnyBlock = primaryEnabled || secondaryEnabled;
                const primaryOk = !primaryEnabled || primaryValid;
                const secondaryOk = !secondaryEnabled || secondaryValid;
                return (
                    !!form.template_name &&
                    TEMPLATE_NAME_RE.test(form.template_name) &&
                    hasAnyBlock &&
                    primaryOk &&
                    secondaryOk
                );
            }
            case 3:
                return !isHighRisk || forceSubmit;
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
    };

    const handlePrev = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const secondaryFilled = secondaryEnabled && secondaryValid;
    // Template "só texto": Foto desabilitada, Texto vira o próprio principal.
    const textOnly = !primaryEnabled && secondaryFilled;

    // Bloco "Foto" tem conteúdo preenchido que seria perdido ao remover.
    const primaryHasContent =
        selectedMedia !== null ||
        originalImageUrl !== null ||
        !!form.title.trim() ||
        !!form.subtitle.trim() ||
        form.buttons.some((b) => !!b.text?.trim() || !!b.url_base?.trim() || !!b.payload?.trim());

    const clearPrimaryBlock = () => {
        setPrimaryEnabled(false);
        setSelectedMedia(null);
        setOriginalImageUrl(null);
        setForm((f) => ({ ...f, title: '', subtitle: '', buttons: [] }));
    };

    const handleRemovePrimary = () => {
        if (primaryHasContent) {
            setConfirmRemovePrimary(true);
            return;
        }
        clearPrimaryBlock();
    };

    const handleRemoveSecondary = () => {
        setSecondaryEnabled(false);
        setSecondaryTitle('');
    };

    // Mesmo padrão do "Adicionar Bloco" do Broadcast: usuário escolhe o
    // tipo de bloco a adicionar, sem nenhum pré-selecionado ao abrir.
    const addBlock = (blockType: 'photo' | 'text') => {
        if (blockType === 'photo') setPrimaryEnabled(true);
        else setSecondaryEnabled(true);
        setIsAddBlockMenuOpen(false);
    };

    const handleSubmit = () => {
        if (!canProceed()) return;

        if (textOnly) {
            onSubmit(
                {
                    page_ids: selectedPages.map((p) => p.page_id),
                    template_name: form.template_name,
                    source_language: form.source_language || undefined,
                    title: secondaryTitle,
                    subtitle: '',
                    buttons: [],
                    force_submit: forceSubmit,
                },
                undefined,
                undefined
            );
            return;
        }

        if (!selectedMedia) return;

        onSubmit(
            {
                page_ids: selectedPages.map((p) => p.page_id),
                template_name: form.template_name,
                source_language: form.source_language || undefined,
                title: form.title,
                subtitle: form.subtitle,
                buttons: form.buttons,
                force_submit: forceSubmit,
            },
            { image_media_id: selectedMedia.id },
            secondaryFilled
                ? {
                      page_ids: selectedPages.map((p) => p.page_id),
                      template_name: `${form.template_name}_texto`,
                      source_language: form.source_language || undefined,
                      title: secondaryTitle,
                      subtitle: '',
                      buttons: [],
                      force_submit: forceSubmit,
                  }
                : undefined
        );
    };

    const previewPageLabel = (() => {
        if (selectedPages.length === 0) return null;
        if (selectedPages.length === 1) return selectedPages[0].page_name;
        return `${selectedPages[0].page_name} e mais ${selectedPages.length - 1}`;
    })();

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-gray-800">Páginas</Label>
                        <CompactPageSelector
                            pages={pages}
                            selectedIds={form.selectedPageIds}
                            onChange={(ids) => setForm((f) => ({ ...f, selectedPageIds: ids }))}
                        />
                    </div>
                );

            case 2: {
                const canAddPrimary = !primaryEnabled;
                const canAddSecondary = !secondaryEnabled;
                return (
                    <div className="space-y-5">
                        <div className="text-center mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">Monte sua Mensagem</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Adicione blocos para compor o conteúdo do template — Foto, Texto, ou ambos.
                            </p>
                        </div>

                        {/* Idioma — auto-detectado, informativo apenas */}
                        {languagesInvolved.length > 0 && (
                            <div className="flex items-start gap-1.5 text-xs text-gray-400 bg-gray-50 rounded-lg px-2.5 py-2">
                                <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-cyan-500" />
                                <p>
                                    O idioma é detectado automaticamente e cada página recebe a tradução no seu
                                    próprio idioma. Destinos:{' '}
                                    <strong className="text-gray-600">{languagesInvolved.join(', ')}</strong>.
                                </p>
                            </div>
                        )}

                        {/* Bloco "Foto": title + subtitle + mídia + botões. */}
                        {primaryEnabled && (
                            <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
                                {/* Block header */}
                                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
                                    <div className="bg-indigo-500 p-1.5 rounded-md">
                                        <ImageIcon className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 flex-1">Foto</span>
                                    <button
                                        type="button"
                                        onClick={handleRemovePrimary}
                                        className="text-gray-400 hover:text-red-500 p-1"
                                        title="Remover bloco de foto"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Block content */}
                                <div className="p-4 space-y-3">
                                    {/* Seletor de imagem */}
                                    {selectedMedia ? (
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={selectedMedia.thumbnail_url ?? selectedMedia.file_url}
                                                alt="Preview"
                                                className="w-20 h-20 object-cover rounded-lg border"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-gray-600 truncate">{selectedMedia.filename}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsMediaLibraryOpen(true)}
                                                    className="text-sm text-[#7dd3fc] hover:text-[#0f172a] mt-1 font-medium"
                                                >
                                                    Trocar arquivo
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setIsMediaLibraryOpen(true)}
                                            className="w-full py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#7dd3fc] hover:bg-[#7dd3fc]/5 transition-colors flex flex-col items-center gap-2"
                                        >
                                            <ImageIcon className="w-8 h-8 text-gray-400" />
                                            <span className="text-sm text-gray-500">
                                                {originalImageUrl
                                                    ? 'Escolher nova imagem (obrigatório ao duplicar)'
                                                    : 'Selecionar foto'}
                                            </span>
                                        </button>
                                    )}

                                    {/* Título e Subtítulo */}
                                    <LocalInput
                                        placeholder="Título (obrigatório)"
                                        value={form.title}
                                        onChange={(v: string) => setForm((f) => ({ ...f, title: v }))}
                                        className="text-sm"
                                        maxLength={80}
                                    />
                                    <LocalInput
                                        placeholder="Subtítulo (obrigatório) — use {{FIRST_NAME}}, {{STATUS}}"
                                        value={form.subtitle}
                                        onChange={(v: string) => setForm((f) => ({ ...f, subtitle: v }))}
                                        className="text-sm"
                                        maxLength={1024}
                                    />
                                </div>

                                {/* Botões */}
                                <div className="border-t border-gray-100">
                                    <ButtonsEditor
                                        buttons={form.buttons}
                                        onChange={(buttons) => setForm((f) => ({ ...f, buttons }))}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Bloco "Texto": mensagem simples, sem imagem/botões.
                            Se coexistir com "Foto", vira um 2º utility template
                            independente enviado em sequência logo após. */}
                        {secondaryEnabled && (
                            <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
                                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
                                    <div className="bg-blue-500 p-1.5 rounded-md">
                                        <Type className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 flex-1">Texto</span>
                                    <span className="text-xs text-gray-400">
                                        {primaryEnabled ? 'complementar, sem botões' : 'principal, sem botões'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleRemoveSecondary}
                                        className="text-gray-400 hover:text-red-500 p-1"
                                        title="Remover bloco de texto"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="p-4 space-y-3">
                                    {/* Messenger renderiza título+subtítulo como
                                        1 mensagem só, sem negrito nem distinção
                                        visual (confirmado testando) — 1 campo
                                        só é mais honesto que forçar 2. */}
                                    <LocalInput
                                        placeholder="Mensagem (obrigatório) — use {{FIRST_NAME}}, {{STATUS}}"
                                        value={secondaryTitle}
                                        onChange={setSecondaryTitle}
                                        className="text-sm"
                                        maxLength={1024}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Adicionar Bloco — mesmo padrão do Broadcast: nenhum
                            bloco pré-selecionado, usuário escolhe o tipo. */}
                        {(canAddPrimary || canAddSecondary) && (
                            isAddBlockMenuOpen ? (
                                <div className="grid grid-cols-2 gap-3">
                                    {canAddPrimary && (
                                        <button
                                            type="button"
                                            onClick={() => addBlock('photo')}
                                            className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-[#7dd3fc] hover:bg-[#7dd3fc]/5 transition-all"
                                        >
                                            <div className="bg-indigo-500 p-2 rounded-lg">
                                                <ImageIcon className="w-5 h-5 text-white" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700">Foto</span>
                                        </button>
                                    )}
                                    {canAddSecondary && (
                                        <button
                                            type="button"
                                            onClick={() => addBlock('text')}
                                            className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-[#7dd3fc] hover:bg-[#7dd3fc]/5 transition-all"
                                        >
                                            <div className="bg-blue-500 p-2 rounded-lg">
                                                <Type className="w-5 h-5 text-white" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700">Texto</span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setIsAddBlockMenuOpen(false)}
                                        className={canAddPrimary && canAddSecondary ? 'col-span-2 text-sm text-gray-400 hover:text-gray-600 py-2' : 'text-sm text-gray-400 hover:text-gray-600 py-2'}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setIsAddBlockMenuOpen(true)}
                                    className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#7dd3fc] hover:bg-[#7dd3fc]/5 transition-colors flex items-center justify-center gap-2 text-gray-500 hover:text-[#0f172a]"
                                >
                                    <Plus className="w-5 h-5" />
                                    <span className="text-sm font-medium">Adicionar Bloco</span>
                                </button>
                            )
                        )}

                        <MediaLibraryModal
                            isOpen={isMediaLibraryOpen}
                            onClose={() => setIsMediaLibraryOpen(false)}
                            onSelect={handleMediaSelect}
                            pageId={selectedPages[0]?.page_id ?? ''}
                            mediaType="image"
                        />
                    </div>
                );
            }

            case 3:
                return (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-gray-200 p-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Nome</span>
                                <span className="font-medium text-gray-900">{form.template_name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Páginas</span>
                                <span className="font-medium text-gray-900">{selectedPages.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Botões</span>
                                <span className="font-medium text-gray-900">{textOnly ? 0 : form.buttons.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Tipo</span>
                                <span className="font-medium text-gray-900">
                                    {textOnly ? 'Só texto (sem imagem)' : 'Com imagem'}
                                </span>
                            </div>
                            {!textOnly && secondaryFilled && (
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Complementar</span>
                                    <span className="font-medium text-gray-900">
                                        {`${form.template_name}_texto`} (só texto)
                                    </span>
                                </div>
                            )}
                        </div>

                        {isHighRisk && (
                            <div className="flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
                                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                <div>
                                    <p className="font-medium">Possível conteúdo de alto risco</p>
                                    <p className="text-xs mt-0.5">
                                        O texto contém termos que podem levar à rejeição. Revise antes de submeter ou
                                        marque abaixo para forçar o envio.
                                    </p>
                                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={forceSubmit}
                                            onChange={(e) => setForceSubmit(e.target.checked)}
                                            className="rounded"
                                        />
                                        <span className="text-xs">Entendo o risco e quero enviar mesmo assim</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 overflow-hidden">
                <div className="flex h-[95vh]">
                    {/* Left Panel - Steps */}
                    <div className="w-2/3 flex flex-col bg-gray-50 border-r border-gray-200">
                        {/* Header com Progress */}
                        <div className="bg-white border-b border-gray-200 p-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                {initialData ? 'Duplicar Utility Template' : 'Criar Novo Utility Template'}
                            </h2>
                            <Input
                                placeholder="Nome do template (ex: promo_black_friday)"
                                value={form.template_name}
                                onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))}
                                className="mb-1 text-sm"
                            />
                            {form.template_name && !TEMPLATE_NAME_RE.test(form.template_name) ? (
                                <p className="text-xs text-destructive mb-3">
                                    Apenas letras minúsculas, números e underscore.
                                </p>
                            ) : (
                                <p className="text-xs text-gray-400 mb-3">Letras minúsculas, números e underscore.</p>
                            )}

                            <div className="flex items-center justify-between">
                                {STEPS.map((step, index) => {
                                    const isActive = currentStep === step.number;
                                    const isCompleted = currentStep > step.number;
                                    return (
                                        <div key={step.number} className="flex items-center flex-1">
                                            <div className="flex flex-col items-center flex-1">
                                                <div
                                                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                                                        isCompleted
                                                            ? 'bg-green-500 text-white'
                                                            : isActive
                                                            ? 'bg-[#7dd3fc] text-white'
                                                            : 'bg-gray-200 text-gray-500'
                                                    }`}
                                                >
                                                    {isCompleted ? <Check className="h-5 w-5" /> : step.number}
                                                </div>
                                                <p
                                                    className={`text-xs mt-2 font-medium ${
                                                        isActive ? 'text-[#0f172a]' : 'text-gray-500'
                                                    }`}
                                                >
                                                    {step.title}
                                                </p>
                                            </div>
                                            {index < STEPS.length - 1 && (
                                                <div
                                                    className={`h-0.5 flex-1 ${
                                                        currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                                                    }`}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Step Content */}
                        <div className="flex-1 overflow-y-auto p-8">{renderStep()}</div>

                        {/* Footer */}
                        <div className="bg-white border-t border-gray-200 p-6 flex justify-between">
                            <Button variant="outline" onClick={handlePrev} disabled={currentStep === 1 || isPending}>
                                <ChevronLeft className="mr-2 h-4 w-4" />
                                Anterior
                            </Button>

                            {currentStep < TOTAL_STEPS ? (
                                <Button
                                    onClick={handleNext}
                                    disabled={!canProceed() || isPending}
                                    className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                                >
                                    Próximo
                                    <ChevronRight className="ml-2 h-4 w-4" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canProceed() || isPending}
                                    className="bg-green-600 hover:bg-green-700"
                                >
                                    {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    {isPending ? 'Criando...' : 'Confirmar e Criar'}
                                    <Check className="ml-2 h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Right Panel - Preview */}
                    <div className="w-1/3 bg-gray-50 overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                            <h3 className="font-semibold text-gray-900 text-center">Preview em Tempo Real</h3>
                            {previewPageLabel && (
                                <div className="mt-3 flex items-center gap-2 justify-center text-sm text-gray-600">
                                    <div className="w-6 h-6 bg-[#0f172a] rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {selectedPages[0].page_name.substring(0, 2).toUpperCase()}
                                    </div>
                                    <span>{previewPageLabel}</span>
                                </div>
                            )}
                        </div>

                        {primaryEnabled && !hasImage && currentStep >= 2 && (
                            <div className="mx-4 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                                <p className="text-xs text-amber-700 font-medium">Selecione uma imagem para continuar</p>
                            </div>
                        )}

                        {primaryEnabled && hasImage && !buttonsValid && currentStep >= 2 && (
                            <div className="mx-4 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                                <p className="text-xs text-amber-700 font-medium">
                                    Preencha o texto e a URL (ou payload) de cada botão para continuar
                                </p>
                            </div>
                        )}

                        <div className="p-6">
                            <UtilityTemplateMessengerPreview
                                pageName={selectedPages[0]?.page_name}
                                primaryTemplate={
                                    primaryEnabled
                                        ? {
                                              title: form.title,
                                              subtitle: form.subtitle,
                                              template_name: form.template_name || 'template',
                                              image_url: originalImageUrl,
                                              thumbnail_url: selectedMedia?.thumbnail_url ?? selectedMedia?.file_url,
                                              buttons: form.buttons,
                                          }
                                        : null
                                }
                                secondaryText={secondaryFilled ? secondaryTitle : null}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>

            <ConfirmDialog
                open={confirmRemovePrimary}
                onOpenChange={setConfirmRemovePrimary}
                title="Remover bloco de foto?"
                description="A imagem, título, subtítulo e botões preenchidos serão perdidos."
                confirmLabel="Remover"
                variant="danger"
                onConfirm={() => {
                    clearPrimaryBlock();
                    setConfirmRemovePrimary(false);
                }}
            />

        </Dialog>
    );
}
